import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getUsers, updateUserGroup, User, CognitoGroup } from '../services/api'
import rhythmicLogo from '../assets/rhythmic-logo-white.svg'

function AdminUsers() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()

  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  useEffect(() => {
    fetchUsers()
  }, [])

  async function fetchUsers() {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getUsers()
      setUsers(data.users)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load users'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleGroupChange(userId: number, newGroup: CognitoGroup) {
    setUpdatingId(userId)
    try {
      const updatedUser = await updateUserGroup(userId, newGroup)
      setUsers(users.map(u => u.id === userId ? updatedUser : u))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update group'
      setError(message)
    } finally {
      setUpdatingId(null)
    }
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <>
      <header className="header">
        <div className="header-brand">
          <img src={rhythmicLogo} alt="Rhythmic" className="header-logo" />
          <span className="header-title">User Management</span>
        </div>
        <div className="header-right">
          <span>{user?.email}</span>
          <span className="badge badge-admin">Admin</span>
          <button className="btn btn-secondary" onClick={() => navigate('/leadership')}>
            Dashboard
          </button>
          <button className="btn btn-secondary" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <main className="main">
        {error && <div className="error">{error}</div>}

        {isLoading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="users-container">
            <div className="users-header">
              <h2>All Users ({users.length})</h2>
              <button className="btn btn-primary" onClick={fetchUsers}>
                Refresh
              </button>
            </div>

            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Group</th>
                  <th>Last Login</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.display_name || '-'}</td>
                    <td>
                      <select
                        value={u.groups?.[0] || 'company'}
                        onChange={e => handleGroupChange(u.id, e.target.value as CognitoGroup)}
                        disabled={updatingId === u.id}
                        className="role-select"
                      >
                        <option value="company">Company</option>
                        <option value="leadership">Leadership</option>
                        <option value="admin">Admin</option>
                      </select>
                      {updatingId === u.id && <span className="updating">...</span>}
                    </td>
                    <td>{formatDate(u.last_login_at)}</td>
                    <td>{formatDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <style>{`
        .users-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .users-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .users-header h2 {
          margin: 0;
          color: var(--rhythmic-dark);
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--rhythmic-white);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .users-table th,
        .users-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--rhythmic-gray-200);
        }

        .users-table th {
          background: var(--rhythmic-gray-100);
          color: var(--rhythmic-gray-700);
          font-weight: 600;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 0.025em;
        }

        .users-table td {
          color: var(--rhythmic-gray-700);
        }

        .users-table tr:hover td {
          background: var(--rhythmic-gray-100);
        }

        .role-select {
          background: var(--rhythmic-white);
          color: var(--rhythmic-gray-700);
          border: 1px solid var(--rhythmic-gray-300);
          border-radius: 4px;
          padding: 6px 10px;
          cursor: pointer;
        }

        .role-select:focus {
          outline: none;
          border-color: var(--rhythmic-orange);
        }

        .role-select:disabled {
          opacity: 0.6;
          cursor: wait;
        }

        .updating {
          margin-left: 8px;
          color: var(--rhythmic-gray-500);
        }
      `}</style>
    </>
  )
}

export default AdminUsers
